/**
 * The signed-in admin's Genesys access token, in memory and in this tab's
 * sessionStorage. Each tab keeps its own token and signs in on its own.
 * The browser SDK cannot refresh a PKCE token, so expiry means a new sign-in.
 */

const SESSION_KEY = 'mediabriz_wizard_genesys_session';

/** Older builds persisted the SDK auth blob here. Removed on load. */
export const LEGACY_TOKEN_KEY = 'premium_app_cloudbriz_auth_data';

/** A token this close to its end is treated as expired before it is sent. */
export const EXPIRY_MARGIN_MAX_MS = 2 * 60 * 1000;

export interface GenesysSession {
  accessToken: string;
  /** Epoch ms from the token response. Null when Genesys did not say. */
  expiresAt: number | null;
  issuedAt: number;
}

let current: GenesysSession | null = null;
const listeners = new Set<() => void>();

function storage(): Storage | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
}

function isSession(value: unknown): value is GenesysSession {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return typeof v.accessToken === 'string' && v.accessToken.length > 0
    && (v.expiresAt === null || typeof v.expiresAt === 'number')
    && typeof v.issuedAt === 'number';
}

/** Smaller of 2 minutes and a quarter of the token's life, so short tokens stay usable. */
export function expiryMarginMs(session: GenesysSession): number {
  if (session.expiresAt === null) return 0;
  const lifetime = Math.max(0, session.expiresAt - session.issuedAt);
  return Math.min(EXPIRY_MARGIN_MAX_MS, lifetime / 4);
}

export function isSessionUsable(session: GenesysSession | null, now = Date.now()): session is GenesysSession {
  if (!session) return false;
  if (session.expiresAt === null) return true;
  return now < session.expiresAt - expiryMarginMs(session);
}

/** Time until the current token stops being usable. Null when there is no expiry to wait for. */
export function msUntilUnusable(now = Date.now()): number | null {
  if (!current || current.expiresAt === null) return null;
  return Math.max(0, current.expiresAt - expiryMarginMs(current) - now);
}

export function setGenesysSession(session: GenesysSession): void {
  current = session;
  try {
    storage()?.setItem(SESSION_KEY, JSON.stringify(session));
  } catch {
    // Storage full or blocked: the token still works for this page.
  }
  listeners.forEach((listener) => listener());
}

export function clearGenesysSession(): void {
  current = null;
  try {
    storage()?.removeItem(SESSION_KEY);
  } catch {
    // Nothing stored.
  }
  listeners.forEach((listener) => listener());
}

/** The stored session for this tab, or null when missing, malformed or near expiry. */
export function readStoredGenesysSession(now = Date.now()): GenesysSession | null {
  try {
    const raw = storage()?.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isSession(parsed) && isSessionUsable(parsed, now) ? parsed : null;
  } catch {
    return null;
  }
}

export function currentGenesysSession(): GenesysSession | null {
  return current;
}

/** The token to send, or '' when there is none or it is about to expire. */
export function usableAccessToken(now = Date.now()): string {
  return isSessionUsable(current, now) ? current.accessToken : '';
}

export function onGenesysSessionChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Drops the token older builds left in localStorage. */
export function clearLegacyGenesysToken(): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.removeItem(LEGACY_TOKEN_KEY);
  } catch {
    // Storage blocked: nothing to clear.
  }
}
